// Comment — top-level + nested-reply DTO returned by /comments
// endpoints. Mirrors the columns on `comments` (db) but applies the
// privacy + visibility scrubs that belong on the wire shape:
//
//   - `authorId` is the registered author's UUID; `null` for guest
//     comments. `authorDisplayName` is the SAFE label (display_name
//     for registered, the guest's nickname for guests). Raw email + IP
//     never appear on this DTO.
//   - `body` is the structured block document — `Block[]` (paragraph +
//     quote-ref only; see primitives/editor-block.ts). The SPA renders
//     it via `renderBlocksToDOM` from `@viquard/editor`; the API does
//     not pre-render to HTML (storing rendered HTML opens a stored-XSS
//     surface for no benefit, and every editing consumer needs the
//     blocks anyway). The closed schema rejects any unknown block kind,
//     attribute key, or inline mark.
//   - `mentions` is denormalised onto the row at submit time. The
//     service walks the inline `<span class="vq-mention" data-entity-id
//     ="UUID">` atoms inside each block's `content` HTML to harvest the
//     resolved user IDs — no displayName-reverse-lookup, the chip
//     already carries the UUID. The display name on the wire shape is
//     guest-safe (never the email).
//   - `viewerReaction` is the requesting viewer's own reaction (`null`
//     for guests / non-reactors); the API never exposes other users'
//     individual reactions.
import { z } from 'zod';
import { ProfileAvatarDesign } from '../members/avatar.js';
import { CommentBody } from '../primitives/editor-block.js';
// ---------------------------------------------------------------------------
// Closed enums — keep in sync with CHECK constraints + ./errors codes.
// ---------------------------------------------------------------------------
export const CommentStatus = z.enum([
    'pending',
    'visible',
    'hidden',
    'spam',
    'deleted',
]);
export const CommentReactionKind = z.enum(['like', 'dislike']);
export const CommentSortOrder = z.enum(['newest', 'most-liked', 'oldest']);
export const CommentReportReason = z.enum([
    'spam',
    'harassment',
    'hate',
    'nsfw',
    'off-topic',
    'other',
]);
export const CommentReportStatus = z.enum(['open', 'resolved', 'dismissed']);
export const CommentModerationMode = z.enum([
    'pre-approve',
    'post',
    'trusted-bypass',
    'closed',
]);
export const ContentBlocklistKind = z.enum([
    'keyword',
    'ip',
    'email',
    'email-domain',
]);
export const ContentBlocklistAction = z.enum(['flag', 'hide']);
// ---------------------------------------------------------------------------
// Attachment descriptor (mirrored on `comments.attachments` JSONB).
// File-system module (plan.md VI) is not yet built, so V1 stores opaque
// URLs only — `mimeType` + `sizeBytes` are required so clients can
// render thumbnails / size labels without a HEAD request.
// ---------------------------------------------------------------------------
export const CommentAttachment = z.object({
    fileId: z.string().uuid().optional(),
    name: z.string().min(1).max(255).optional(),
    url: z
        .string()
        .min(1)
        .max(2048)
        .refine((value) => {
        try {
            const parsed = new URL(value);
            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        }
        catch {
            return value.startsWith('/api/');
        }
    }, 'expected http(s) URL or API content path'),
    mimeType: z.string().min(1).max(128),
    sizeBytes: z.number().int().nonnegative(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    alt: z.string().max(200).optional(),
});
// ---------------------------------------------------------------------------
// Mention — resolved user reference attached to the wire shape so the
// SPA can render `@<displayName>` chips without an extra round-trip.
// ---------------------------------------------------------------------------
export const CommentMention = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
});
// ---------------------------------------------------------------------------
// Comment — canonical wire shape.
// ---------------------------------------------------------------------------
export const Comment = z.object({
    id: z.string().uuid(),
    articleId: z.string().uuid(),
    // ---- thread structure ----
    parentId: z.string().uuid().nullable(),
    threadRootId: z.string().uuid(),
    depth: z.number().int().min(0).max(8),
    // ---- authorship (registered XOR guest) ----
    /** Registered author's UUID; null for guest comments. */
    authorId: z.string().uuid().nullable(),
    /** Safe display label: registered user's display_name, or the
     *  guest's submitted nickname. Never the email or IP. */
    authorDisplayName: z.string(),
    /** Registered author's current profile avatar design. Null for guest comments. */
    authorAvatarDesign: ProfileAvatarDesign.nullable().optional(),
    /** Whether this row is a guest comment (true) or a registered-user
     *  comment (false). Surfaced separately from `authorId === null` so
     *  the SPA can render a guest badge without a null-check that's
     *  semantically a guest-check. */
    isGuest: z.boolean(),
    // ---- content ----
    /** Structured legacy Block[] or guarded DOM-Editor v2 comment document.
     *  Validated against `CommentBody` from
     *  `@personal-blog/contracts/primitives`. */
    body: CommentBody,
    mentions: z.array(CommentMention),
    attachments: z.array(CommentAttachment),
    // ---- moderation ----
    status: CommentStatus,
    /** Reason string for the most recent moderation transition. Surfaced
     *  to manager+ in the moderation queue; null for visible rows seen
     *  by ordinary readers. */
    moderationReason: z.string().nullable(),
    // ---- counters ----
    likeCount: z.number().int().nonnegative(),
    dislikeCount: z.number().int().nonnegative(),
    reportCount: z.number().int().nonnegative(),
    // ---- viewer-relative annotations ----
    /** The viewer's own reaction on this comment, or `null` for guests /
     *  non-reactors. The API never exposes other users' reactions. */
    viewerReaction: CommentReactionKind.nullable(),
    /** Whether the viewer can edit/delete (author OR article author OR
     *  manager+). Null for guests. */
    viewerCanWrite: z.boolean().nullable(),
    /** Whether the viewer has reported this comment in an unresolved
     *  report. Null for guests. */
    viewerHasReported: z.boolean().nullable(),
    // ---- audit ----
    /** First-edit marker — null until first edit, ISO datetime after. */
    editedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// CommentModerationSettings — wire shape for /admin/articles/:slug/
// comments/settings.
// ---------------------------------------------------------------------------
export const CommentModerationSettings = z.object({
    articleId: z.string().uuid(),
    mode: CommentModerationMode,
    locked: z.boolean(),
    lockedAt: z.string().datetime({ offset: true }).nullable(),
    lockedBy: z.string().uuid().nullable(),
    lockReason: z.string().nullable(),
    notifyAuthor: z.boolean(),
});
// ---------------------------------------------------------------------------
// CommentReport — admin-only DTO for /admin/comments/reports.
// ---------------------------------------------------------------------------
export const CommentReport = z.object({
    id: z.string().uuid(),
    commentId: z.string().uuid(),
    reporterId: z.string().uuid().nullable(),
    reporterDisplayName: z.string().nullable(),
    reporterAvatarDesign: ProfileAvatarDesign.nullable().optional(),
    reason: CommentReportReason,
    details: z.string().nullable(),
    status: CommentReportStatus,
    resolvedBy: z.string().uuid().nullable(),
    resolvedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// ContentBlocklistRow — admin-only DTO for /admin/comments/blocklist.
// For exact IP/email entries, `value` is a keyed hash; keyword and
// email-domain entries remain human-readable.
// ---------------------------------------------------------------------------
export const ContentBlocklistEntry = z.object({
    id: z.string().uuid(),
    kind: ContentBlocklistKind,
    value: z.string(),
    action: ContentBlocklistAction,
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=comment.js.map